import {Table, Tag, Tooltip} from 'antd';
import {GET_RECURRING_INVESTMENT_LOGS} from './resolvers';
import {useState} from 'react';
import {toast} from 'react-toastify';
import {useQuery} from '@apollo/client';
import styles from './recurringInvestment.module.scss';

export const RecurringInvestmentLogs = ({id}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const {
    data: logsData,
    loading,
    refetch,
  } = useQuery(GET_RECURRING_INVESTMENT_LOGS, {
    variables: {
      filter: {
        recurringInvestmentId: id,
      },
    },
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const handlePageChange = (page, pageSize) => {
    if (page === currentPage && pageSize === limit) return;
    setCurrentPage(page);
    setLimit(pageSize);
    refetch({
      filter: {
        page,
        limit: pageSize,
        recurringInvestmentId: id,
      },
    });
  };

  return (
    <div>
      <h1>Recurring Investment Logs</h1>
      <Table
        loading={loading}
        className={styles.table}
        dataSource={logsData?.getRecurringInvestmentLogs?.data}
        columns={logsColumns}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: logsData?.getRecurringInvestmentLogs?.pagination?.total,
          onChange: handlePageChange,
        }}
      />
    </div>
  );
};

const logsColumns = [
  {
    title: 'Order ID',
    key: 'id',
    dataIndex: 'id',
    render: (text, render) => (
      <span>
        <b>{render?.order?.id}</b>
      </span>
    ),
  },
  {
    title: 'Ticker',
    key: 'symbol',
    dataIndex: 'symbol',
    render: (text, render) => <span>{render?.order?.symbol}</span>,
  },
  {
    title: 'Direction',
    key: 'direction',
    dataIndex: 'direction',
    render: (text, render) => <span>{render?.order?.direction}</span>,
  },
  {
    title: 'Instrument',
    key: 'instrumentKey',
    dataIndex: 'instrumentKey',
    render: (text, render) => (
      <Tag
        color={
          {
            'BOND': 'cyan',
            'STOCK': 'red',
            'MUTUAL_FUND': 'purple',
          }[render?.order?.instrumentKey]
        }>
        {render?.order?.instrumentKey?.replace(/_/g, ' ')}
      </Tag>
    ),
  },
  {
    title: 'Recurring Investment Status',
    key: 'status',
    dataIndex: 'status',
    render: (text, render) => (
      <Tag
        color={
          {
            'SUCCESS': 'green',
            'PENDING': 'yellow',
            'FAILED': 'red',
          }[render?.status]
        }>
        {render?.status}
      </Tag>
    ),
  },

  {
    title: 'Currency',
    key: 'assetCurrency',
    dataIndex: 'assetCurrency',
    render: (text, render) => <span>{render?.order?.assetCurrency}</span>,
  },
  {
    title: 'Amount Difference',
    key: 'amountDifference',
    dataIndex: 'amountDifference',
    render: (text, render) => (
      <span>{render?.amountDifference?.toLocaleString()}</span>
    ),
  },
  {
    title: 'Processed Amount',
    key: 'processedAmount',
    dataIndex: 'processedAmount',
    render: (text, render) => (
      <span>{render?.processedAmount?.toLocaleString()}</span>
    ),
  },
  {
    title: 'Reason',
    key: 'reason',
    dataIndex: 'reason',
    render: (text, render) => {
      if (!render?.reason) return '-';
      return (
        <Tooltip title={render?.reason}>
          <span>
            {render?.reason.length > 20
              ? `${render?.reason.substring(0, 20)}...`
              : render?.reason}
          </span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Type',
    key: 'type',
    dataIndex: 'type',
    render: (text, render) => (
      <Tag color={render?.type === 'LIMIT' ? 'black' : 'default'}>
        {render?.type}
      </Tag>
    ),
  },

  {
    title: 'Order Price',
    key: 'price',
    dataIndex: 'price',
    render: (text, render) => (
      <span>{render?.order?.price?.toLocaleString()}</span>
    ),
  },
  {
    title: 'Order Fee Charged',
    key: 'feeCharged',
    dataIndex: 'feeCharged',
    render: (text, render) => (
      <span>{render?.order?.feeCharged?.toFixed(2)}</span>
    ),
  },
  {
    title: 'Quantity',
    key: 'quantity',
    dataIndex: 'quantity',
    render: (text, record) => <span>{record?.order?.quantity}</span>,
  },
  {
    title: 'Order Status',
    key: 'orderStatus',
    dataIndex: 'orderStatus',
    render: (text, render) => (
      <Tag
        color={
          {
            'FILLED': 'green',
            'PENDING': 'orange',
            'ERROR': 'red',
            'REJECTED': 'red',
            'CANCELLED': 'red',
            'SETTLED': 'cyan',
            'PARTIALLY_FILLED': 'yellow',
          }[render?.order?.status] || 'blue'
        }
        key={render?.order?.id}>
        {render?.order?.status}
      </Tag>
    ),
  },
];
